import { Box, Typography, useTheme } from '@mui/material';
import ExoAvatar from '../exo/ExoAvatar';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { tokens } from '../../global/theme/tokens';
import { useEffect, useRef, useState } from 'react';
import ExoDialog from '../exo/ExoDialog';
import { ExoVariants } from '../exo/ExoVariants';
import { NewPersonLead } from '../new/NewPersonLead';
import { NewCompanyLead } from '../new/NewCompanyLead';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import Cookies from 'universal-cookie';
import AddIcon from '@mui/icons-material/Add';
import { fontSizes } from '../exo/rich-text-editor/Editor';

export const ECSenderPreview = ({
  sender,
  type,
  avatarSize = '35px',
  to,
  cc,
  subject,
  email,
}) => {
  const theme = useTheme();
  const cookies = new Cookies();

  const [open, setOpen] = useState(false);
  const leadsCompaniesRef = useRef(null);
  const leadsPersonsRef = useRef(null);
  const [variants, setVariants] = useState([]);

  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  useEffect(() => {
    setVariants([
      {
        label: t('Lead Company'),
        Icon: AddBusinessIcon,
        content: (
          <NewCompanyLead
            onClose={() => setOpen(false)}
            onCreate={(data) => handleChange(leadsCompaniesRef, data)}
            onUpdate={(data) => handleChange(leadsCompaniesRef, data)}
            startData={{
              responsibleUser: {
                id: cookies.get('userId'),
              },
              emails: [
                {
                  emailAddress: sender?.email,
                  isInvoiceMail: true,
                  isPrimary: true,
                },
              ],
            }}
          />
        ),
      },
      {
        label: t('Person'),
        Icon: PersonAddAlt1Icon,
        content: (
          <NewPersonLead
            onClose={() => setOpen(false)}
            onCreate={(data) => handleChange(leadsPersonsRef, data)}
            onUpdate={(data) => handleChange(leadsPersonsRef, data)}
            startData={{
              responsibleUser: {
                id: cookies.get('userId'),
              },
              emails: [
                {
                  emailAddress: sender?.email,
                  isInvoiceMail: true,
                  isPrimary: true,
                },
              ],
            }}
          />
        ),
      },
    ]);
  }, [leadsCompaniesRef, leadsPersonsRef]);

  function handleChange(ref, data) {
    if (!ref) return;
    ref.current.addRow(data);
  }

  const { id } = sender || {};

  const senderTypeMap = {
    companyLead: {
      label: `${sender?.nameShort || sender?.name}`,
      to: `/leads/companies/${id}`,
      picture: sender?.logo?.temporaryUrl,
    },
    company: {
      label: `${sender?.nameShort || sender?.name}`,
      to: `/companies/${id}`,
      picture: sender?.logo?.temporaryUrl,
    },
    personLead: {
      label: `${sender?.firstName} ${sender?.lastName}`,
      to: `/leads/persons/${id}`,
    },
    contact: {
      label: `${sender?.firstName} ${sender?.lastName}`,
      to: `/contacts/${id}`,
    },
    user: {
      label: `${sender?.firstName} ${sender?.lastName}`,
      to: `/employees/${sender?.id}`,
      picture: sender?.picture?.temporaryUrl,
    },
  };

  const navigate = useNavigate();

  function handleClickAvatar() {
    const route = senderTypeMap[type]?.to;
    navigate(route);
  }

  return (
    <Box className="w-full flex items-start gap-2 overflow-hidden">
      <ExoAvatar
        onClick={
          senderTypeMap[type]?.to ? handleClickAvatar : () => setOpen(true)
        }
        size={avatarSize}
        className=" shadow-sm"
        icon={!senderTypeMap[type]?.label && <AddIcon />}
        color={!senderTypeMap[type]?.label && colors.grey[400]}
        name={senderTypeMap[type]?.label || ' '}
        picture={senderTypeMap[type]?.picture || null}
        type={
          type === 'company' || type === 'companyLead' ? 'company' : 'contact'
        }
      />
      <Box className="flex flex-col justify-center h-full w-full overflow-hidden">
        <Typography
          fontWeight={600}
          textAlign="start"
          className="w-full text-ellipsis whitespace-nowrap overflow-hidden"
        >
          {senderTypeMap[type]?.label || sender?.name}
          {email && (
            <>
              <br />
              <span
                style={{ fontSize: '11px', fontWeight: 400 }}
                className=" opacity-50"
              >{`<${email}>`}</span>
            </>
          )}
        </Typography>
        {to && to.length > 0 && (
          <Typography fontSize={12} className="pt-1">
            <span style={{ fontWeight: 500 }}>{t('to')}:</span>
            {to.map((email) => (
              <span className="pl-2 opacity-50">{`<${email}>`}</span>
            ))}
          </Typography>
        )}
        {cc && cc.length > 0 && (
          <Typography fontSize={12}>
            <span style={{ fontWeight: 500 }}>{t('cc')}:</span>
            {cc.map((email) => (
              <span className="pl-2 opacity-50">{`<${email}>`}</span>
            ))}
          </Typography>
        )}
        {subject && (
          <Typography
            textAlign="start"
            fontSize="11px"
            fontWeight={500}
            className="w-full text-ellipsis overflow-hidden whitespace-nowrap"
          >
            {subject}
          </Typography>
        )}
      </Box>
      <ExoDialog open={open} onClose={() => setOpen(false)} limitWidth>
        <ExoVariants variants={variants} />
      </ExoDialog>
    </Box>
  );
};
